import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { CommonModule } from '@angular/common';

import {
  NgbCarousel,
  NgbCarouselConfig,
  NgbCarouselModule,
  NgbSlideEvent
} from '@ng-bootstrap/ng-bootstrap';

import { AnalyticsService } from '../../../../shared/services/analytics.service';
import { StatsigService } from 'app/shared/services/statsig.service';

import { ButtonComponent } from '../../../../shared/components/button/button.component';
import { SvgIconComponent } from '../../../../shared/components/svg-icon/svg-icon.component';
import { WatermarkComponent } from 'app/shared/components/watermark/watermark.component';
import { FeedbackControlsComponent } from 'app/components/feedback-controls/feedback-controls.component';
import { ImageComponent } from '../../../../shared/components/image/image.component';

import { MediaPreviewCloseDirective } from 'app/shared/directives/media-preview-close.directive';

import { IMediaData, IMediaFile } from 'app/interfaces/conversations.interfaces';
import { IButtonConfig } from '../../../../shared/interfaces/button.interfaces';
import { ISvgConfig } from '../../../../shared/interfaces/svg.interfaces';
import { IWatermarkConfig } from 'app/shared/interfaces/media.interfaces';

import { ESvgTypes } from '../../../../shared/enums/svg.enums';
import { EButtonTypes } from '../../../../shared/enums/button.enums';
import { ECreatorMediaTypes } from '../../../../enums/creator.enums';
import { EChatStates } from '../../../../enums/conversation.enums';
import { EFeatureGates } from 'app/shared/enums/feature-gates.enums';
import { EImageUsage } from 'app/shared/enums/image-usage.enums';

@Component({
  selector: 'stxt-message-media',
  standalone: true,
  imports: [
    CommonModule,
    NgbCarouselModule,
    ButtonComponent,
    SvgIconComponent,
    WatermarkComponent,
    FeedbackControlsComponent,
    ImageComponent,
    MediaPreviewCloseDirective
  ],
  templateUrl: './message-media.component.html',
  styleUrl: './message-media.component.scss',
  providers: [NgbCarouselConfig]
})
export class MessageMediaComponent implements OnInit, OnChanges {
  @Input() media: IMediaData;
  @Input() userId: string;
  @Input() chatState: EChatStates;
  @Input() creatorName: string;
  @Input() creatorId: string;
  @Output() imagesLoaded: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() isPreviewMode: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() openPayment: EventEmitter<IMediaData> = new EventEmitter<IMediaData>();

  @ViewChild('carousel') set carouselRef(carousel: NgbCarousel) {
    if (carousel) {
      this._carousel = carousel;
      this.onCarouselReady();
    }
  }

  private _carousel: NgbCarousel;
  mediaFullScreen: boolean = false;
  mediaOwned: boolean;
  isMediaLoaded: boolean = false;
  unlockBtn: IButtonConfig = { text: '10 tokens to unlock', fill: EButtonTypes.SecondaryEmpty };
  viewBtn: IButtonConfig = { text: 'View', fill: EButtonTypes.SecondaryDark };
  closeSvg: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'close' };
  lockSvg: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'lock' };
  tokenUnlockSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'token-unlock' };
  leftNavigation: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'arrow_left' };
  rightNavigation: ISvgConfig = { fill: ESvgTypes.OutlinedDark, name: 'arrow_right' };
  imageSvg: ISvgConfig = { fill: ESvgTypes.None, name: 'photo' };
  currentSlide: string;
  isFeedbackControlsEnabled: boolean;
  protected readonly EImageUsage = EImageUsage;

  constructor(
    config: NgbCarouselConfig,
    private readonly cdr: ChangeDetectorRef,
    private readonly analyticsService: AnalyticsService,
    private readonly statsigService: StatsigService
  ) {
    config.wrap = false;
    config.interval = 0;
    config.keyboard = false;
    config.pauseOnHover = false;
    config.animation = false;
  }

  ngOnInit(): void {
    this.checkMediasQuantity();
    this.mediaOwned = this.isMediaOwned();
    this.checkStatsigFeatureGate(EFeatureGates.FEEDBACK_CONTROLS_MEDIA);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['media']) {
      this.mediaOwned = this.isMediaOwned();
    }
  }

  async checkStatsigFeatureGate(featureGate: EFeatureGates): Promise<void> {
    await this.statsigService.waitForInitialization();
    this.isFeedbackControlsEnabled = await this.statsigService.checkFeatureGate(featureGate);
  }

  isMediaOwned(): boolean {
    return this.media?.files?.every(file => file?.owned) ?? false;
  }

  checkMediasQuantity(): void {
    if (this.media?.photos === 1) return;
    this.viewBtn.text = 'View all';
    this.unlockBtn.text = 'Unlock all';
  }

  togglePreview(value: boolean, image?: IMediaFile): void {
    this.isPreviewMode.emit(value);
    this.mediaFullScreen = value;

    if (value) {
      this.onCarouselReady();
      this.trackAnalyticsEvent(image);
    }
  }

  trackAnalyticsEvent(image: IMediaFile): void {
    this.analyticsService.mediaClicked(
      this.userId,
      this.creatorName,
      this.chatState,
      this.creatorId,
      image.url,
      ECreatorMediaTypes.Image
    );
  }

  onCarouselReady(): void {
    if (this._carousel && this.mediaFullScreen) {
      this._carousel.select(this.currentSlide);
      this.cdr.detectChanges();
    }
  }

  onSlide($event: NgbSlideEvent): void {
    this.currentSlide = $event.current;
  }

  getWatermarkConfig(): IWatermarkConfig {
    return {
      creatorName: this.creatorName || '',
      creatorId: this.creatorId || '',
      showWatermark: true
    };
  }

  unlockImage(): void {
    const isUnlocked = this.mediaOwned || this.media?.price === 0;
    const firstFile = this.media?.files?.[0];

    isUnlocked && firstFile
      ? this.togglePreview(true, firstFile)
      : this.openPayment.emit(this.media);
  }

  onOverlayClick(): void {
    this.togglePreview(false);
  }
}
